import React from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { DashLg, ArrowsFullscreen, FullscreenExit, ChevronUp, StopFill } from "react-bootstrap-icons";

// The floating conversation widget, docked bottom-right. It only ever shows
// the question/answer TEXT (narrative + refusal) - charts/tables render in
// the main page area instead (see AnalyticsChat.js), so the widget stays
// small and readable while the visuals get real screen space.
//
// Resizing is hand-rolled, not the browser's native CSS `resize` - the
// widget is anchored via `right`/`bottom`, so a native resize handle (always
// drawn at an element's own bottom-right corner) would sit almost exactly on
// the screen's corner: hard to grab, and worse, once the browser has taken
// over the element's size that way, toggling a CSS class for minimize no
// longer reliably overrides it. Size is instead plain React state, adjusted
// by dragging a small handle at the widget's TOP-LEFT corner (the opposite,
// on-screen corner from the anchor point) - which also means minimize always
// works, since there is no browser-owned size left to fight with.
const MIN_WIDTH = 300;
const MIN_HEIGHT = 240;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 800;

const ChatWidget = ({ turns, draft, setDraft, isBusy, onSend, onStop }) => {
  const [mode, setMode] = React.useState("normal"); // normal | minimized | maximized
  const [size, setSize] = React.useState({ width: 380, height: 520 });
  const feedEndRef = React.useRef(null);
  const dragStateRef = React.useRef(null);

  React.useEffect(() => {
    if (mode !== "minimized") {
      feedEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [turns, mode]);

  const onResizeHandlePointerDown = (e) => {
    if (mode !== "normal") return;
    e.preventDefault();
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: size.width,
      startHeight: size.height,
    };

    const onPointerMove = (moveEvent) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      // The handle is top-left; the box is anchored bottom-right, so
      // dragging the handle LEFT/UP (negative delta) should GROW the box,
      // and dragging it RIGHT/DOWN should shrink it - hence the sign flip.
      const deltaX = drag.startX - moveEvent.clientX;
      const deltaY = drag.startY - moveEvent.clientY;
      setSize({
        width: Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, drag.startWidth + deltaX)),
        height: Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, drag.startHeight + deltaY)),
      });
    };

    const onPointerUp = () => {
      dragStateRef.current = null;
      window.removeEventListener("mousemove", onPointerMove);
      window.removeEventListener("mouseup", onPointerUp);
    };

    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("mouseup", onPointerUp);
  };

  const style = mode === "normal" ? { width: size.width, height: size.height } : undefined;

  return (
    <div className={`analytics-chat-widget analytics-chat-widget--${mode}`} style={style}>
      {mode === "normal" && (
        <div
          className="analytics-chat-widget-resize-handle"
          onMouseDown={onResizeHandlePointerDown}
          title="Drag to resize"
        />
      )}
      <div
        className="analytics-chat-widget-header"
        onDoubleClick={() => setMode((m) => (m === "minimized" ? "normal" : "minimized"))}
      >
        <span className="fw-semibold">Analytics Chat</span>
        <div className="analytics-chat-widget-controls">
          <button
            type="button"
            className="analytics-chat-widget-btn"
            title={mode === "minimized" ? "Restore" : "Minimize"}
            onClick={() => setMode((m) => (m === "minimized" ? "normal" : "minimized"))}
          >
            {mode === "minimized" ? <ChevronUp size={20} /> : <DashLg size={22} />}
          </button>
          <button
            type="button"
            className="analytics-chat-widget-btn"
            title={mode === "maximized" ? "Restore" : "Maximize"}
            onClick={() =>
              setMode((m) => (m === "maximized" ? "normal" : "maximized"))
            }
          >
            {mode === "maximized" ? <FullscreenExit size={20} /> : <ArrowsFullscreen size={20} />}
          </button>
        </div>
      </div>

      {mode !== "minimized" && (
        <div className="analytics-chat-widget-body">
          <div className="analytics-chat-feed">
            {turns.length === 0 && (
              <div className="text-muted small">Ask a question about your fleet to get started.</div>
            )}

            {turns.map((turn) => (
              <div key={turn.localId} className="analytics-chat-turn mb-3">
                <div className="analytics-chat-bubble analytics-chat-bubble--question">{turn.question}</div>

                <div className="analytics-chat-bubble analytics-chat-bubble--answer">
                  {turn.status === "sending" && (
                    <div className="text-muted small">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sending...
                    </div>
                  )}

                  {turn.narrative && <p className="analytics-chat-narrative">{turn.narrative}</p>}

                  {turn.surfaceId && !turn.refusal && (
                    <div className="text-muted small fst-italic">
                      Chart added to the analytics screen &rarr;
                    </div>
                  )}

                  {turn.refusal && (
                    // Plain styling, not an alert/warning box: this text is as often a normal
                    // conversational reply (greetings, "what can you do") as an actual refusal -
                    // see the backend's system prompt for why both share this path.
                    <p className="analytics-chat-narrative analytics-chat-refusal mb-0">{turn.refusal}</p>
                  )}

                  {turn.status === "streaming" && (
                    <div className="text-muted small mt-1 d-flex align-items-center gap-2">
                      <Spinner animation="border" size="sm" />
                      Thinking...
                      <button
                        type="button"
                        className="analytics-chat-stop-btn"
                        title="Stop this question"
                        onClick={() => onStop?.(turn)}
                      >
                        <StopFill size={12} className="me-1" />
                        Stop
                      </button>
                    </div>
                  )}

                  {turn.status === "stopped" && (
                    <div className="text-muted small mt-1 fst-italic">Stopped.</div>
                  )}

                  {turn.status === "error" && (
                    <div className="text-danger small mt-1">Something went wrong with this question.</div>
                  )}
                </div>
              </div>
            ))}
            <div ref={feedEndRef} />
          </div>

          <Form onSubmit={onSend} className="analytics-chat-input d-flex gap-2">
            <Form.Control
              as="textarea"
              rows={1}
              placeholder="Ask a question..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSend(e);
                }
              }}
              disabled={isBusy}
            />
            <Button type="submit" size="sm" disabled={isBusy || !draft.trim()}>
              Send
            </Button>
          </Form>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
